import mixpanel from 'mixpanel-browser';
import { getStartupID } from '.';
import Cookies from 'js-cookie';

// // Replace with your actual Mixpanel token
const MIXPANEL_TOKEN = process.env.REACT_APP_MXP_PROJECT_TOKEN;
// Read on every call: admin Single Sign-On sets the cookie after this file has loaded,
// with no page reload, and logging in clears it the same way.
const isMixpanelDisabled = () => Cookies.get("IS_MIXPANEL_DISABLED") === "true" || process.env.REACT_APP_IS_MIXPANEL_DISABLED;

let isMixpanelReady = false;

// Starts Mixpanel the first time tracking is allowed, so an admin session never starts it.
const canTrack = () => {
    if (isMixpanelDisabled()) return false;
    if (!isMixpanelReady) {
        mixpanel.init(MIXPANEL_TOKEN, {
            debug: false, // Shows logs in browser console
            persistence: 'localStorage',
        });
        isMixpanelReady = true;
    }
    return true;
};

export const trackEvent = (eventName, properties = {}) => {
    if (canTrack()) {
        mixpanel.track(eventName, properties);
    }
    else {
        console.log("Mixpanel Tracking Disabled", eventName, properties);
    }
};

/**
 * Reads utm_campaign and utm_source from cookies first,
 * then falls back to the current URL search params.
 */
export const getUtmProperties = () => {
    const params = new URLSearchParams(window.location.search);
    return {
        utm_campaign: Cookies.get('utm_campaign') || params.get('utm_campaign') || undefined,
        utm_source: Cookies.get('utm_source') || params.get('utm_source') || undefined,
    };
};

export const MixPanelOnboardingTracker = (step, values, options = {}, trackName = "onboarding") => {
    const startup_id = getStartupID();
    const {
        isSkipped = false,
        countries = [],
        states = [],
        cities = [],
        stages = [],
        categories = [],
        sectors = [],
        businessModels = [],
        roundTypes = [],
        currencies = []
    } = options;

    let requestMixPanel = {};
    let eventName = '';

    // Handle the Thank You Redirection Screen (special case outside of step flow)
    if (step === 'thank_you') {
        requestMixPanel = {
            // ...(startup_id ? { startup_id } : {}),
            "profile_source": values.profile_source,
            ...(values?.profile_source === "dashboard" ? { "is_profile_completed": true } : {}),
            timestamp: new Date().toISOString(),
            ...getUtmProperties(),
        };

        try {
            trackEvent('Onboarding - Thank You Redirection Screen', { ...requestMixPanel });
        } catch (error) {
            console.error('Error tracking Thank You screen:', error);
        }
        return;
    }

    // Common properties for all numbered steps
    const commonProps = {
        ...(startup_id ? { startup_id, email: values?.email } : { email: values?.email }),
        timestamp: new Date().toISOString(),
        step_number: step,
        total_steps: 11,
        progression: `${Math.round((step / 11) * 100)}%`,
        ...getUtmProperties(),
    };

    // Handle skipped steps
    if (isSkipped) {
        switch (step) {
            case 1:
                eventName = 'Skipped - Contact Information';
                break;
            case 2:
                eventName = 'Skipped - Select Country';
                break;
            case 3:
                eventName = 'Skipped - Select State & City';
                break;
            case 4:
                eventName = 'Skipped - Startup Information';
                break;
            case 5:
                eventName = 'Skipped - Startup Logo, Entity & Formation Date';
                break;
            case 6:
                eventName = 'Skipped - Startup Sector & Model';
                break;
            case 7:
                eventName = 'Skipped - Fundraising Details';
                break;
            case 8:
                eventName = 'Skipped - Elevator Pitch';
                break;
            case 9:
                eventName = 'Skipped - LinkedIn URL';
                break;
            case 10:
                eventName = 'Skipped - Founder Details';
                break;
            case 11:
                eventName = 'Skipped - Co-Founders';
                break;
            default:
                eventName = `Skipped - Step ${step}`;
                break;
        }

        requestMixPanel = {
            ...commonProps,
            skipped: true
        };

        try {
            trackEvent(eventName, requestMixPanel);
        } catch (error) {
            console.error(`Error tracking skipped step ${step}:`, error);
        }
        return;
    }

    // Regular step tracking
    try {
        if (trackName === "onboarding") {
            switch (step) {
                case 1:
                    requestMixPanel = {
                        ...commonProps,
                        "first_name": values.first_name,
                        "last_name": values.last_name,
                        "mobile_number": values?.phone_code + "-" + values?.mobile_number,
                        // "phone_code": values?.phone_code,
                    };
                    eventName = 'Onboarding - Contact Information';
                    break;
                case 2:
                    const country = countries.find(country => country?._id === values.country_id);
                    requestMixPanel = {
                        ...commonProps,
                        // "country_id": values.country_id,
                        "country": country?.label || 'Unknown Country',
                    };
                    eventName = 'Onboarding - Select Country';
                    break;
                case 3:
                    const state = states.find(state => state?._id === values.state_id);
                    const city = cities.find(city => city?._id === values.city_id);
                    requestMixPanel = {
                        ...commonProps,
                        // // "state_id": values.state_id,
                        // // "city_id": values.city_id,
                        "state": state?.label || 'Unknown State',
                        "city": city?.label || 'Unknown City',
                    };
                    eventName = 'Onboarding - Select State & City';
                    break;
                case 4:
                    const stage = stages.find(stage => stage?._id === values.stage_id);
                    requestMixPanel = {
                        ...commonProps,
                        // "startup_name": values.startup_name,
                        // "website_url": values.website_url,
                        // // "stage_id": values.stage_id,
                        // "stage_name": stage?.label || 'Unknown Stage',
                        // "raised_round": values.raised_round,
                    };
                    eventName = 'Onboarding - Startup Information';
                    break;
                case 5:
                    requestMixPanel = {
                        ...commonProps,
                        // ...(typeof values.startup_logo !== 'string' ? { startup_logo: true } : values.startup_logo !== null ? { startup_logo: true } : { startup_logo: false }),
                        // ...(values?.entity_name ? { entity_name: values.entity_name } : {}),
                        // ...(values?.formation_date ? { formation_date: values.formation_date } : {}),
                    };
                    eventName = 'Onboarding - Startup Logo, Entity & Formation Date';
                    break;
                case 6:
                    const category = categories.find(category => category?._id === values.category_id);
                    // console.log(category, "category")
                    const sector = sectors.find(sector => sector?._id === values.sector_id);
                    // console.log(values.business_model_id, "model_id")
                    const businessModelNames = businessModels.filter(model => String(values.business_model_id)?.includes(model?._id))?.map(item => item?.name);
                    // ?.join(",");
                    // console.log(businessModels.filter(model => String(values.business_model_id)?.includes(model?._id))?.map(item => item?.name), "dd")
                    requestMixPanel = {
                        ...commonProps,
                        // // "category_id": values.category_id,
                        // "category_name": category?.name || 'Unknown Category',
                        // // "sector_id": values.sector_id,
                        // "sector_name": sector?.name || 'Unknown Sector',
                        // // "business_model_id": values.business_model_id,
                        // "business_model_name": businessModelNames || 'Unknown Business Models',
                    };
                    // console.log(requestMixPanel,"requestMixPanel")
                    eventName = 'Onboarding - Startup Sector & Model';
                    break;
                case 7:
                    const currency = currencies.find(currency => currency?._id === values.currency_id);
                    // console.log(roundTypes, "roundTypes", currencies)
                    const round_type_name = roundTypes?.find(type => type?._id === values.round_type)
                    requestMixPanel = {
                        ...commonProps,
                        // ...(typeof values.pitch_deck !== 'string' ? { pitch_deck: true } : values?.pitch_deck !== null ? { pitch_deck: true } : { pitch_deck: true }),
                        // "round_name": values.round_name,
                        // // "round_type": values.round_type,
                        // "round_type": round_type_name?.name,
                        // "total_round_size": Number(String(values.total_round_size)?.replaceAll(",", "")) || 0,
                        // // "currency_id": values.currency_id,
                        // "currency_code": currency?.name || 'Unknown Currency',
                    };

                    eventName = 'Onboarding - Round Details';
                    break;
                case 8:
                    requestMixPanel = {
                        ...commonProps,
                        // ...(typeof values.elevator_pitch !== 'string' ? { elevator_pitch: values.elevator_pitch } : {}),
                        // "problem_solving": values.problem_solving,
                        // "description": values.description,
                    };
                    eventName = 'Onboarding - Elevator Pitch, Startup Overview';
                    break;
                case 9:
                    requestMixPanel = {
                        ...commonProps,
                        // "linkedin_url": values.linkedin_url,
                    };
                    eventName = 'Onboarding - LinkedIn URL';
                    break;
                case 10:
                    requestMixPanel = {
                        ...commonProps,
                        // "founder_name": values?.founder?.name,
                        // "founder_bio": values?.founder?.bio,
                        // "founder_title": values?.founder?.title,
                        // "founder_logo": values?.founder?.image,
                        // "founder_linkedin_url": values?.founder?.linkedin_url,
                    };
                    eventName = 'Onboarding - Founder Details';
                    break;
                case 11:
                    requestMixPanel = {
                        ...commonProps,
                        // "coFounders": values.coFoundersArray?.map((cf, idx) => ({
                        //     name: cf.name,
                        //     title: cf.title,
                        //     bio: cf.bio,
                        //     linkedin_url: cf.linkedin_url,
                        //     image: cf.image,
                        // })),
                        // "coFounders_count": values.coFoundersArray?.length || 0
                    };
                    eventName = 'Onboarding - Co-Founders';
                    break;
                default:
                    eventName = `Onboarding - Step ${step}`;
                    requestMixPanel = commonProps;
                    break;
            }
        }
        else if (trackName === "upload-pitch-deck") {
            switch (step) {
                case 5:
                    const category = categories.find(category => category?._id === values.category_id);
                    // console.log(category, "category")
                    const sector = sectors.find(sector => sector?._id === values.sector_id);
                    // console.log(values.business_model_id, "model_id")
                    const businessModelNames = businessModels.filter(model => String(values.business_model_id)?.includes(model?._id))?.map(item => item?.name);
                    // ?.join(",");
                    // console.log(businessModels.filter(model => String(values.business_model_id)?.includes(model?._id))?.map(item => item?.name), "dd")
                    requestMixPanel = {
                        ...commonProps,
                        // // "category_id": values.category_id,
                        // "category_name": category?.name || 'Unknown Category',
                        // // "sector_id": values.sector_id,
                        // "sector_name": sector?.name || 'Unknown Sector',
                        // // "business_model_id": values.business_model_id,
                        // "business_model_name": businessModelNames || 'Unknown Business Models',
                    };
                    // console.log(requestMixPanel,"requestMixPanel")
                    eventName = 'Upload Pitch Deck - Startup Sector & Model';
                    break;
                case 6:
                    const currency = currencies.find(currency => currency?._id === values.currency_id);
                    // console.log(roundTypes, "roundTypes", currencies)
                    const round_type_name = roundTypes?.find(type => type?._id === values.round_type)
                    requestMixPanel = {
                        ...commonProps,
                        // ...(typeof values.pitch_deck !== 'string' ? { pitch_deck: true } : values?.pitch_deck !== null ? { pitch_deck: true } : { pitch_deck: true }),
                        // "round_name": values.round_name,
                        // // "round_type": values.round_type,
                        // "round_type": round_type_name?.name,
                        // "total_round_size": Number(String(values.total_round_size)?.replaceAll(",", "")) || 0,
                        // // "currency_id": values.currency_id,
                        // "currency_code": currency?.name || 'Unknown Currency',
                    };

                    eventName = 'Upload Pitch Deck - Round Details';
                    break;
                default:
                    eventName = `Upload Pitch Deck - Step ${step}`;
                    requestMixPanel = commonProps;
                    break;
            }
        }
        else {

        }
        // Add time spent if provided
        if (values?.stepStartTime) {
            requestMixPanel.time_spent_ms = new Date() - values.stepStartTime;
            requestMixPanel.time_spent_seconds = Math.round(requestMixPanel.time_spent_ms / 1000);
        }
        trackEvent(eventName, requestMixPanel);
    } catch (error) {
        console.error(`Error tracking step ${step}:`, error);
    }
};

// Onboarding v2 collapses the old 11-step flow into 2 steps, so it needs its own
// step/total numbering rather than MixPanelOnboardingTracker's hardcoded 11.
// `startup_id` is passed in explicitly for step 1: the id only lands in the
// cookie after the save response is handled, so getStartupID() is still empty
// at the moment we track that step.
const ONBOARDING_V2_TOTAL_STEPS = 2;

export const MixPanelOnboardingV2Tracker = (step, values, options = {}) => {
    const {
        startup_id = getStartupID(),
        countries = [],
        states = [],
        cities = [],
        stages = [],
    } = options;

    try {
        const commonProps = {
            ...(startup_id ? { startup_id } : {}),
            email: values?.email || Cookies.get('email'),
            timestamp: new Date().toISOString(),
            step_number: step,
            total_steps: ONBOARDING_V2_TOTAL_STEPS,
            progression: `${Math.round((step / ONBOARDING_V2_TOTAL_STEPS) * 100)}%`,
            ...getUtmProperties(),
        };

        if (values?.stepStartTime) {
            commonProps.time_spent_ms = new Date() - values.stepStartTime;
            commonProps.time_spent_seconds = Math.round(commonProps.time_spent_ms / 1000);
        }

        if (step === 1) {
            trackEvent('Onboarding - Contact Information', {
                ...commonProps,
                "first_name": values?.first_name,
                "last_name": values?.last_name,
                "mobile_number": values?.phone_code + "-" + values?.mobile_number,
                "country": countries.find(country => country?._id === values?.country_id)?.label,
                "state": states.find(state => state?._id === values?.state_id)?.label,
                "city": cities.find(city => city?._id === values?.city_id)?.label,
            });
            return;
        }

        const stage = stages.find(stage => stage?._id === values?.stage_id);
        const startupProps = {
            ...commonProps,
            "startup_name": values?.startup_name,
            "stage_name": stage?.name || stage?.label || 'Unknown Stage',
            "raised_round": values?.raised_round,
            "website_url": values?.website_url || undefined,
            "entity_name": values?.entity_name || undefined,
            "formation_date": values?.formation_date || undefined,
            "startup_logo": !!values?.startup_logo,
        };

        trackEvent('Onboarding - Startup Information', startupProps);
        // Step 2 is the last step, so a successful save also completes the profile.
        trackEvent('Onboarding - Profile Completed!', startupProps);
    } catch (error) {
        console.error(`Error tracking onboarding v2 step ${step}:`, error);
    }
};

export const logUserIn = (userData) => {
    // Other login logic...

    if (canTrack()) {
        // For existing users
        mixpanel.identify(userData._id);

        // Set user properties
        mixpanel.people.set({
            '$email': userData.email,
            // '$name': userData.name,
            'Refresh Date': new Date().toISOString()
        });
    }
};

export const signUserUp = (userData) => {
    // Other signup logic...
    if (canTrack()) {
        // For new users - first identify with new ID
        mixpanel.identify(userData._id);

        // Then create an alias
        mixpanel.alias(userData._id);

        // Set initial properties
        mixpanel.people.set({
            '$email': userData.email,
            // '$name': userData.name,
            'Refresh Date': new Date().toISOString()
        });
    }
};

export const HandleUser = ({ savedData, startup_name }) => {
    if (canTrack()) {
        const currentDistinctId = mixpanel.get_distinct_id();

        // Check if current user is already identified
        if (currentDistinctId !== savedData.user_id) {
            // If not the same user, identify them
            mixpanel.identify(savedData.user_id);

            // Set user profile only once
            mixpanel.people.set_once({
                ...(savedData.first_name && savedData.last_name && {
                    $name: `${savedData.first_name} ${savedData.last_name}`,
                }),
                _id: savedData.user_id,
                $email: savedData.email
            });

            trackEvent('User Logged In', {
                startup_name: startup_name,
                name: `${savedData.first_name} ${savedData.last_name}`,
                email: savedData.email,
            });
        }
    }
}


// // Export the mixpanel instance directly
export default mixpanel;